import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { env } from '../config/env'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipRetry?: boolean
    /**
     * Sends a request without credentials and prevents a 401 response from
     * starting the refresh/login flow. Use for content that is intentionally
     * available to signed-out visitors.
     */
    anonymous?: boolean
    /**
     * Keeps credentials when available, but lets an optional request fail
     * without redirecting the current page to login.
     */
    skipAuthRedirect?: boolean
  }
}

// API Configuration
// Force proxy usage in development to avoid CORS issues
const API_BASE_URL = import.meta.env.DEV ? '/api' : (env('VITE_API_BASE_URL') || '')

// In development, calls to the legacydb service can optionally hit a local backend
// directly. Set VITE_LEGACY_DB_LOCAL=true in .env to enable. When false (default),
// legacydb calls go through the Vite proxy to the hosted dev API.
const LEGACY_DB_DEV_BASE_URL = 'http://localhost:8080'
const USE_LOCAL_LEGACY_DB = import.meta.env.VITE_LEGACY_DB_LOCAL === 'true'

// In development, security calls route through the Vite proxy to the local
// security API. Set VITE_SECURITY_LOCAL=true only when you intentionally want
// the browser to call the local backend directly instead of using the proxy.
const SECURITY_DEV_BASE_URL = 'https://localhost:7222'
const USE_LOCAL_SECURITY = import.meta.env.VITE_SECURITY_LOCAL === 'true'

// In development, user calls route through the Vite proxy to the hosted dev API.
// Set VITE_USER_LOCAL=true only when you intentionally want the browser to call
// the local user API directly instead of using the proxy. Defaults to false.
const USER_DEV_BASE_URL = env('VITE_USER_API_BASE_URL') || 'https://localhost:5001'
const USE_LOCAL_USER = import.meta.env.VITE_USER_LOCAL === 'true'

// In development, session calls route through the Vite proxy to the hosted dev API.
// Set VITE_SESSION_LOCAL=true only when you intentionally want the browser to call
// the local session API directly instead of using the proxy. Defaults to false.
const SESSION_DEV_BASE_URL = env('VITE_SESSION_API_BASE_URL') || 'https://localhost:5004'
const USE_LOCAL_SESSION = import.meta.env.VITE_SESSION_LOCAL === 'true'

// In development, Canvas calls route through the Vite proxy to the hosted dev API.
// Set VITE_CANVAS_LOCAL=true only when you intentionally want the browser to call
// the local Canvas API directly instead of using the proxy. Defaults to false.
const CANVAS_DEV_BASE_URL = env('VITE_CANVAS_API_BASE_URL') || 'https://localhost:5040'
const USE_LOCAL_CANVAS = import.meta.env.VITE_CANVAS_LOCAL === 'true'

// Retry configuration for unstable API
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  retryableStatuses: [408, 502, 503, 504],
}

function isRetryableError(error: any): boolean {
  if (error.code === 'ECONNABORTED') return true
  if (error.code === 'ERR_NETWORK') return true
  if (error.code === 'ETIMEDOUT') return true
  if (error.response?.status && RETRY_CONFIG.retryableStatuses.includes(error.response.status)) {
    return true
  }
  return false
}

function getRetryDelay(attempt: number): number {
  const exponential = Math.min(RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt), RETRY_CONFIG.maxDelayMs)
  return exponential + Math.random() * 1000
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function isEmployeeSSOExchangeUrl(url: string | undefined): boolean {
  return !!url && /^(?:\/security)?\/[^/]+\/employee-sso(?:[?#]|$)/.test(url)
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken && !config.anonymous && !isEmployeeSSOExchangeUrl(config.url)) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    // Inject tenant into all service paths: /{service}/{tenant}/...
    const tenant = env('VITE_TENANT_ID')
    const services = ['/legacydb/', '/user/', '/session/', '/security/', '/events/', '/transcript/', '/scorm/', '/canvas/']
    for (const prefix of services) {
      if (config.url?.startsWith(prefix) && !config.url.includes(`/${tenant}/`)) {
        config.url = config.url.replace(prefix, `${prefix}${tenant}/`)
        break
      }
    }

    // In development, optionally route legacydb calls to local backend.
    if (import.meta.env.DEV && USE_LOCAL_LEGACY_DB && config.url?.includes('/legacydb/')) {
      config.baseURL = LEGACY_DB_DEV_BASE_URL
    }

    // In development, optionally route security calls to local backend.
    if (import.meta.env.DEV && USE_LOCAL_SECURITY && config.url?.includes('/security/')) {
      config.baseURL = SECURITY_DEV_BASE_URL
    }

    // In development, optionally route user calls to local backend.
    if (import.meta.env.DEV && USE_LOCAL_USER && config.url?.includes('/user/')) {
      config.baseURL = USER_DEV_BASE_URL
    }

    // In development, optionally route session calls to local backend.
    if (import.meta.env.DEV && USE_LOCAL_SESSION && config.url?.includes('/session/')) {
      config.baseURL = SESSION_DEV_BASE_URL
    }

    // In development, optionally route Canvas calls to local backend.
    if (import.meta.env.DEV && USE_LOCAL_CANVAS && config.url?.includes('/canvas/')) {
      config.baseURL = CANVAS_DEV_BASE_URL
      config.url = config.url.replace(/^\/canvas/, '')
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Retry on timeout / network / 5xx gateway errors with exponential backoff
    if (!originalRequest.skipRetry && isRetryableError(error)) {
      originalRequest._retryCount = originalRequest._retryCount || 0

      if (originalRequest._retryCount < RETRY_CONFIG.maxRetries) {
        originalRequest._retryCount++
        const delay = getRetryDelay(originalRequest._retryCount - 1)
        console.warn(
          `[API Retry] Attempt ${originalRequest._retryCount}/${RETRY_CONFIG.maxRetries} ` +
          `for ${originalRequest.method?.toUpperCase()} ${originalRequest.url} — ` +
          `waiting ${Math.round(delay)}ms (${error.code || error.response?.status})`
        )
        await sleep(delay)
        return apiClient(originalRequest)
      }

      console.error(
        `[API Retry] All ${RETRY_CONFIG.maxRetries} retries exhausted for ` +
        `${originalRequest.method?.toUpperCase()} ${originalRequest.url}`
      )
    }

    // Handle 401 auth errors
    if (
      error.response?.status === 401 &&
      !originalRequest.anonymous &&
      !originalRequest.skipAuthRedirect &&
      !originalRequest._retry &&
      !isEmployeeSSOExchangeUrl(originalRequest.url)
    ) {
      // Don't retry if this is already a refresh-token call (prevents infinite loop)
      if (originalRequest.url?.includes('refresh-token')) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
      
      originalRequest._retry = true
      
      // Try to refresh token
      const refreshToken = localStorage.getItem('refreshToken')
      
      if (refreshToken) {
        try {
          const { authService } = await import('./authService')
          const response = await authService.refreshToken(refreshToken)
          
          if (response.success && response.accessToken) {
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`
            return apiClient(originalRequest)
          }
        } catch (refreshError) {
          // Refresh failed, clear storage and redirect
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          
          // Only redirect if we're not already on the login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login'
          }
          return Promise.reject(refreshError)
        }
      }
      
      // No refresh token, clear storage and redirect
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      
      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Generic API methods
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config).then((response) => response.data),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config).then((response) => response.data),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config).then((response) => response.data),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config).then((response) => response.data),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config).then((response) => response.data),
}

export default apiClient
